import { useCallback, useMemo, useRef } from 'react';
import {
  MAP_MIN_LOT_ZOOM,
  PARCEL_FILTER_POINTS_MIN_ZOOM,
  PARCEL_FILTER_POINTS_MAX_ZOOM,
  PARCEL_LAYER_STYLE,
  PARCEL_POINTS_LAYER_STYLE,
  HOVER_LOT_STYLE,
  SELECTED_LOT_GLOW_STYLE,
  EMPTY_SELECTED_LOT_FILTER,
  buildSelectedLotIdFilter,
  parcelSelectedGlowLayerId,
} from '../styles/mapTheme';
import { getSignedPmtilesUrl, refreshPmtilesVectorSourceIfNeeded } from '../../Utils/pmtilesAuth';
import { canAccessMapStyle } from '../utils/canAccessMapStyle';
import { moveReferencePlacesForeground } from '../utils/referencePlaces';
import { viewportIntersectsBbox } from '../data/cordobaProvinceCoverage';
import {
  PARCEL_POINTS_SOURCE_LAYER,
  getParcelPointsTilesets,
  isParcelPointsTileset,
  parcelPointsLayerId,
  parcelPointsSourceId,
} from '../data/parcelFilterSections';

const DEFAULT_PMTILES_CONFIG_URL =
  'https://s3.us-east-1.amazonaws.com/argenlot.lots/tiles/config/pmtiles_index.json';

const PMTILES_CONFIG_URL =
  process.env.REACT_APP_PMTILES_INDEX_URL || DEFAULT_PMTILES_CONFIG_URL;

const PM_TILES_ENSURE_MOVE_DEBOUNCE_MS = 200;

/** `source-layer` de los tilesets de polígonos; el índice puede sobreescribirlo con `layer`. */
const PARCEL_POLYGONS_SOURCE_LAYER = 'parcelas';

/** Tope de visibilidad de capas MapLibre para el tier más alto (overzoom de tiles). */
const PARCEL_LAYER_OVERZOOM_MAX = 24;

/**
 * El índice declara los `high` desde z14, pero ese zoom es el que degrada la
 * geometría en zonas densas. Los tiles `low` (hasta z13) se ven bien: se
 * dejan en overzoom durante z14 y el high recién pinta en 15.
 */
const PARCEL_HIGH_MIN_ZOOM = 15;

function debounce(fn, waitMs) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), waitMs);
  };
}

function effectiveMinZoom(cfg) {
  return cfg.minzoom >= 14 ? Math.max(cfg.minzoom, PARCEL_HIGH_MIN_ZOOM) : cfg.minzoom;
}

function nextTierMinZoom(cfg, allConfigs) {
  return allConfigs
    .filter((other) => other.id !== cfg.id && effectiveMinZoom(other) > cfg.maxzoom)
    .reduce((min, other) => Math.min(min, effectiveMinZoom(other)), Infinity);
}

/**
 * maxzoom del *layer* MapLibre (exclusivo: se oculta con zoom >= maxzoom).
 * El maxzoom del índice/source es inclusivo (último zoom con tiles).
 * Tier superior → 24 para overzoom; si hay un tier siguiente, corta en su minzoom.
 */
function getParcelLayerMaxZoom(cfg, allConfigs) {
  const nextTierMin = nextTierMinZoom(cfg, allConfigs);
  if (Number.isFinite(nextTierMin)) return nextTierMin;
  return PARCEL_LAYER_OVERZOOM_MAX;
}

/** ¿El zoom actual debe usar este tileset? Incluye overzoom del tier más alto. */
function configMatchesTileZoom(cfg, tileZoom, allConfigs) {
  const minZoom = effectiveMinZoom(cfg);
  if (tileZoom < minZoom) return false;
  if (tileZoom <= cfg.maxzoom) return true;
  const nextTierMin = nextTierMinZoom(cfg, allConfigs);
  return !Number.isFinite(nextTierMin) || tileZoom < nextTierMin;
}

function parcelPolygonLayerIds(id) {
  return [
    `self-hosted-vector-fill-${id}`,
    `self-hosted-vector-layer-${id}`,
    `self-hosted-vector-hover-line-${id}`,
    parcelSelectedGlowLayerId(id),
  ];
}

function applyParcelLayerZoomRange(map, cfg, allConfigs) {
  if (!map || !cfg) return;
  const minZoom = effectiveMinZoom(cfg);
  const maxZoom = getParcelLayerMaxZoom(cfg, allConfigs);
  parcelPolygonLayerIds(cfg.id).forEach((layerId) => {
    if (map.getLayer(layerId)) map.setLayerZoomRange(layerId, minZoom, maxZoom);
  });
}

/** Entradas de polígonos del índice: las de puntos se cargan aparte. */
function getPolygonConfigs(configs) {
  return configs.filter((cfg) => !isParcelPointsTileset(cfg));
}

/** Configs de polígonos que corresponden al centro y zoom actuales. */
function getMatchingPolygonConfigs(map, polygonConfigs) {
  const center = map.getCenter();
  const tileZoom = Math.floor(map.getZoom());
  const inBounds = (cfg) => (
    center.lng >= cfg.bounds[0]
    && center.lng <= cfg.bounds[2]
    && center.lat >= cfg.bounds[1]
    && center.lat <= cfg.bounds[3]
  );
  return polygonConfigs.filter(
    (cfg) => configMatchesTileZoom(cfg, tileZoom, polygonConfigs) && inBounds(cfg),
  );
}

/** URL del source vectorial: TileJSON/http local sin firma; pmtiles:// S3 vía getpmtilesurl. */
async function resolveVectorSourceUrl(sourceUrl) {
  if (!sourceUrl) return sourceUrl;
  if (sourceUrl.startsWith('http://') || sourceUrl.startsWith('https://')) {
    return sourceUrl;
  }
  return getSignedPmtilesUrl(sourceUrl);
}

/** Resuelve el objeto paint; tolera refs que guardaron la función en vez del resultado. */
function resolveActivePaint(getter) {
  if (!getter) return null;
  let paint = getter();
  if (typeof paint === 'function') paint = paint();
  if (!paint || paint.fillColor == null) return null;
  return paint;
}

function ensureParcelSelectedGlowLayer(map, {
  selectedGlowId,
  sourceId,
  sourceLayer,
  layerMinZoom,
  layerMaxZoom,
  layerVisibility,
  getActivePaint,
}) {
  if (!map || map.getLayer(selectedGlowId)) return;
  const paint = resolveActivePaint(getActivePaint);
  const selectedFilter = paint?.selectedLotId
    ? buildSelectedLotIdFilter(paint.selectedLotId)
    : EMPTY_SELECTED_LOT_FILTER;
  map.addLayer({
    id: selectedGlowId,
    type: 'line',
    source: sourceId,
    'source-layer': sourceLayer,
    minzoom: layerMinZoom,
    maxzoom: layerMaxZoom,
    layout: { visibility: layerVisibility },
    filter: selectedFilter,
    paint: {
      'line-color': SELECTED_LOT_GLOW_STYLE.glowColor,
      'line-width': SELECTED_LOT_GLOW_STYLE.glowWidth,
      'line-blur': SELECTED_LOT_GLOW_STYLE.glowBlur,
      'line-opacity': SELECTED_LOT_GLOW_STYLE.glowOpacity,
    },
  });
}

/**
 * Maneja la carga dinámica de capas PMTiles por departamento/provincia.
 * Expone funciones para:
 *  - fetchPmtilesConfig: carga el JSON de configuración desde S3
 *  - ensurePmtilesLayerForView: agrega capas PMTiles según la vista actual del mapa
 *  - toggleParcelas: muestra/oculta todas las capas PMTiles cargadas
 *  - applyParcelasFilter: aplica un filtro de estado a las capas PMTiles
 * @param {object} options
 * @param {Function} options.getActivePaintExpressions
 *   Función que retorna las expresiones de estilos activas (del hook useParcelFilters).
 *   Se llama al agregar cada capa nueva para sincronizar el estilo vigente.
 */
export function usePmtilesLayers({ getActivePaintExpressions, reapplyPointsFiltersRef } = {}) {
  const pmtilesConfigRef    = useRef([]);
  const loadedPmtilesIdsRef = useRef(new Set());
  const parcelasVisibleRef  = useRef(true);
  /** Tilesets de puntos del índice (uno por provincia). */
  const pointsTilesetsRef = useRef([]);
  const loadedPointsIdsRef = useRef(new Set());
  const pointsSourceUrlsRef = useRef(new Map());
  const pointsSourceSigningRef = useRef(new Map());
  const filterViewStateRef = useRef({ hasActiveFilters: false });
  const getActivePaintRef = useRef(getActivePaintExpressions);
  getActivePaintRef.current = getActivePaintExpressions;

  /**
   * Polígonos visibles si parcelas ON, zoom ≥ mínimo y no estamos en modo puntos filtrados.
   * Puntos: por provincia, si parcelas ON, hay filtros activos, zoom < 12 y su bbox está en viewport.
   */
  const syncParcelLayersVisibility = (map) => {
    if (!canAccessMapStyle(map)) return;

    const zoom = map.getZoom();
    const hasFilters = filterViewStateRef.current.hasActiveFilters;
    const pointsAllowed = parcelasVisibleRef.current
      && hasFilters
      && zoom < PARCEL_FILTER_POINTS_MAX_ZOOM;

    let showPoints = false;
    pointsTilesetsRef.current.forEach((cfg) => {
      const layerId = parcelPointsLayerId(cfg.id);
      if (!map.getLayer(layerId)) return;
      const visible = pointsAllowed && viewportIntersectsBbox(map, cfg.bounds);
      map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
      if (visible) showPoints = true;
    });

    const showPolygons = parcelasVisibleRef.current
      && zoom >= MAP_MIN_LOT_ZOOM
      && !showPoints;
    const polygonVis = showPolygons ? 'visible' : 'none';

    loadedPmtilesIdsRef.current.forEach((id) => {
      const fillId = `self-hosted-vector-fill-${id}`;
      const lineId = `self-hosted-vector-layer-${id}`;
      const hoverId = `self-hosted-vector-hover-line-${id}`;
      const selectedGlowId = parcelSelectedGlowLayerId(id);
      if (map.getLayer(fillId)) map.setLayoutProperty(fillId, 'visibility', polygonVis);
      if (map.getLayer(lineId)) map.setLayoutProperty(lineId, 'visibility', polygonVis);
      if (map.getLayer(hoverId)) map.setLayoutProperty(hoverId, 'visibility', polygonVis);
      if (map.getLayer(selectedGlowId)) {
        map.setLayoutProperty(selectedGlowId, 'visibility', polygonVis);
      }
    });
  };

  /** Firmas de puntos en vuelo por tileset: dos ensure concurrentes comparten la misma. */
  const resolvePointsSourceUrl = (cfg) => {
    const inFlight = pointsSourceSigningRef.current;
    if (!inFlight.has(cfg.id)) {
      inFlight.set(
        cfg.id,
        resolveVectorSourceUrl(cfg.url).finally(() => inFlight.delete(cfg.id)),
      );
    }
    return inFlight.get(cfg.id);
  };

  /** Crea source+layer de puntos para cada provincia cuyo bbox toca el viewport. */
  const ensureParcelPointsLayers = useCallback(async (map) => {
    if (!canAccessMapStyle(map)) return;

    for (const cfg of pointsTilesetsRef.current) {
      if (!viewportIntersectsBbox(map, cfg.bounds)) continue;

      const sourceId = parcelPointsSourceId(cfg.id);
      const layerId = parcelPointsLayerId(cfg.id);

      if (map.getSource(sourceId) && pointsSourceUrlsRef.current.get(cfg.id) !== cfg.url) {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
        map.removeSource(sourceId);
      }

      if (!map.getSource(sourceId)) {
        const resolvedUrl = await resolvePointsSourceUrl(cfg);
        if (!resolvedUrl) continue;
        // La firma tarda: el mapa pudo desmontarse en el medio.
        if (!canAccessMapStyle(map)) return;
        // Otro ensure concurrente pudo crear el source mientras se firmaba la URL:
        // sin este segundo chequeo, MapLibre lanza "Source already exists".
        if (!map.getSource(sourceId)) {
          map.addSource(sourceId, {
            type: 'vector',
            url: resolvedUrl,
            minzoom: cfg.minzoom ?? PARCEL_FILTER_POINTS_MIN_ZOOM,
            maxzoom: cfg.maxzoom ?? PARCEL_FILTER_POINTS_MAX_ZOOM,
            promoteId: 'id',
          });
          pointsSourceUrlsRef.current.set(cfg.id, cfg.url);
        }
      } else if (!cfg.url?.startsWith('http')) {
        // La firma de S3 dura 5 min: sin renovarla, los rangos que MapLibre pide
        // después (al moverse a otra provincia o cambiar de zoom) dan 403.
        void refreshPmtilesVectorSourceIfNeeded(map, sourceId, cfg.url);
      }

      if (!map.getLayer(layerId)) {
        map.addLayer({
          id: layerId,
          type: 'circle',
          source: sourceId,
          'source-layer': cfg.layer || PARCEL_POINTS_SOURCE_LAYER,
          minzoom: PARCEL_FILTER_POINTS_MIN_ZOOM,
          maxzoom: PARCEL_FILTER_POINTS_MAX_ZOOM,
          layout: { visibility: 'none' },
          paint: {
            'circle-radius': PARCEL_POINTS_LAYER_STYLE.circleRadius,
            'circle-color': PARCEL_POINTS_LAYER_STYLE.circleColor,
            'circle-opacity': PARCEL_POINTS_LAYER_STYLE.circleOpacity,
            'circle-stroke-width': PARCEL_POINTS_LAYER_STYLE.circleStrokeWidth,
            'circle-stroke-color': PARCEL_POINTS_LAYER_STYLE.circleStrokeColor,
          },
        });

        const paint = resolveActivePaint(getActivePaintRef.current);
        if (paint?.pointsCircleColor) {
          map.setPaintProperty(layerId, 'circle-color', paint.pointsCircleColor);
        }
      }

      map.moveLayer(layerId);
      loadedPointsIdsRef.current.add(cfg.id);
    }

    moveReferencePlacesForeground(map);
  }, []);

  const fetchPmtilesConfig = async () => {
    try {
      const response = await fetch(PMTILES_CONFIG_URL);
      if (!response.ok) throw new Error(`Error al cargar config PMTiles: ${response.status}`);
      const config = await response.json();
      pmtilesConfigRef.current = config;
      pointsTilesetsRef.current = getParcelPointsTilesets(config);
      return config;
    } catch (error) {
      console.error('Error cargando PMTiles config:', error);
      pmtilesConfigRef.current = [];
      pointsTilesetsRef.current = [];
      return [];
    }
  };

  const ensurePmtilesLayerForView = useCallback(async (map) => {
    if (!canAccessMapStyle(map)) return;

    if (pointsTilesetsRef.current.length > 0) {
      await ensureParcelPointsLayers(map);
      if (!canAccessMapStyle(map)) return;
      if (filterViewStateRef.current.hasActiveFilters) {
        reapplyPointsFiltersRef?.current?.();
      }
    }

    const tileZoom = Math.floor(map.getZoom());

    if (tileZoom >= MAP_MIN_LOT_ZOOM) {
      for (const id of loadedPmtilesIdsRef.current) {
        const cfg = pmtilesConfigRef.current.find((c) => c.id === id);
        if (cfg && !cfg.url?.startsWith('http')) {
          void refreshPmtilesVectorSourceIfNeeded(map, `self-hosted-vector-${id}`, cfg.url);
        }
      }
    }

    if (tileZoom < MAP_MIN_LOT_ZOOM) {
      syncParcelLayersVisibility(map);
      return;
    }

    // Los tilesets de puntos comparten índice pero tienen otro `source-layer`:
    // se cargan aparte en ensureParcelPointsLayers.
    const polygonConfigs = getPolygonConfigs(pmtilesConfigRef.current);
    const matches = getMatchingPolygonConfigs(map, polygonConfigs);

    for (const cfg of matches) {
      const { id } = cfg;
      const sourceLayer = cfg.layer || PARCEL_POLYGONS_SOURCE_LAYER;
      const sourceId    = `self-hosted-vector-${id}`;
      const fillLayerId = `self-hosted-vector-fill-${id}`;
      const layerId     = `self-hosted-vector-layer-${id}`;
      const hoverLineId = `self-hosted-vector-hover-line-${id}`;
      const selectedGlowId = parcelSelectedGlowLayerId(id);
      const layerMinZoom = effectiveMinZoom(cfg);
      const layerMaxZoom = getParcelLayerMaxZoom(cfg, polygonConfigs);

      if (
        loadedPmtilesIdsRef.current.has(id) &&
        map.getSource(sourceId) &&
        map.getLayer(fillLayerId) &&
        map.getLayer(layerId)
      ) {
        // Capas ya creadas con maxzoom del tileset (p. ej. 18) se ocultaban al llegar ahí.
        applyParcelLayerZoomRange(map, cfg, polygonConfigs);
        ensureParcelSelectedGlowLayer(map, {
          selectedGlowId,
          sourceId,
          sourceLayer,
          layerMinZoom,
          layerMaxZoom,
          layerVisibility: parcelasVisibleRef.current ? 'visible' : 'none',
          getActivePaint: getActivePaintRef.current,
        });
        continue;
      }

      const resolvedUrl = await resolveVectorSourceUrl(cfg.url);
      if (!resolvedUrl) continue;
      // Ídem: firmar la URL es asíncrono y el mapa pudo irse.
      if (!canAccessMapStyle(map)) return;

      if (!map.getSource(sourceId)) {
        map.addSource(sourceId, {
          type: 'vector',
          url: resolvedUrl,
          minzoom: cfg.minzoom,
          // Source maxzoom = último zoom con tiles; MapLibre overzoomea por encima.
          maxzoom: cfg.maxzoom,
          promoteId: 'id',
        });
      }

      const layerVisibility = parcelasVisibleRef.current ? 'visible' : 'none';

      if (!map.getLayer(fillLayerId)) {
        map.addLayer({
          id: fillLayerId,
          type: 'fill',
          source: sourceId,
          'source-layer': sourceLayer,
          minzoom: layerMinZoom,
          maxzoom: layerMaxZoom,
          layout: { visibility: layerVisibility },
          paint: {
            'fill-color': PARCEL_LAYER_STYLE.fillColor,
            'fill-opacity': PARCEL_LAYER_STYLE.fillOpacity,
          },
        });

        const paint = resolveActivePaint(getActivePaintRef.current);
        if (paint) {
          map.setPaintProperty(fillLayerId, 'fill-color', paint.fillColor);
          map.setPaintProperty(fillLayerId, 'fill-opacity', paint.fillOpacity);
          if (Object.prototype.hasOwnProperty.call(paint, 'layerFilter')) {
            map.setFilter(fillLayerId, paint.layerFilter);
          }
        }
      }

      if (!map.getLayer(layerId)) {
        map.addLayer({
          id: layerId,
          type: 'line',
          source: sourceId,
          'source-layer': sourceLayer,
          minzoom: layerMinZoom,
          maxzoom: layerMaxZoom,
          layout: { visibility: layerVisibility },
          paint: {
            'line-color': PARCEL_LAYER_STYLE.lineColor,
            'line-opacity': PARCEL_LAYER_STYLE.lineOpacity,
            'line-width': PARCEL_LAYER_STYLE.lineWidth,
          },
        });

        const paint = resolveActivePaint(getActivePaintRef.current);
        if (paint) {
          map.setPaintProperty(layerId, 'line-color', paint.lineColor);
          map.setPaintProperty(layerId, 'line-width', paint.lineWidth);
          if (Object.prototype.hasOwnProperty.call(paint, 'layerFilter')) {
            map.setFilter(layerId, paint.layerFilter);
          }
        }
      }

      if (!map.getLayer(hoverLineId)) {
        map.addLayer({
          id: hoverLineId,
          type: 'line',
          source: sourceId,
          'source-layer': sourceLayer,
          minzoom: layerMinZoom,
          maxzoom: layerMaxZoom,
          layout: { visibility: layerVisibility },
          paint: {
            'line-color': HOVER_LOT_STYLE.borderColor,
            'line-width': HOVER_LOT_STYLE.borderWidth,
            'line-opacity': [
              'case',
              ['boolean', ['feature-state', 'hover'], false],
              1,
              0,
            ],
          },
        });
      }

      ensureParcelSelectedGlowLayer(map, {
        selectedGlowId,
        sourceId,
        sourceLayer,
        layerMinZoom,
        layerMaxZoom,
        layerVisibility,
        getActivePaint: getActivePaintRef.current,
      });

      loadedPmtilesIdsRef.current.add(id);
    }

    // Capas high ya montadas (minzoom 14 del índice) seguirían pintando a z14
    // si no les actualizamos el rango: el corte nuevo es 15.
    polygonConfigs.forEach((cfg) => {
      if (loadedPmtilesIdsRef.current.has(cfg.id)) {
        applyParcelLayerZoomRange(map, cfg, polygonConfigs);
      }
    });

    moveReferencePlacesForeground(map);

    syncParcelLayersVisibility(map);
  }, [ensureParcelPointsLayers, reapplyPointsFiltersRef]);

  const scheduleEnsurePmtilesLayerForView = useMemo(
    () => debounce((map) => void ensurePmtilesLayerForView(map), PM_TILES_ENSURE_MOVE_DEBOUNCE_MS),
    [ensurePmtilesLayerForView]
  );

  // toggleParcelas, applyParcelasFilter, getInteractiveParcelLayerIds
  // quedan exactamente igual que antes — no cambian
  const toggleParcelas = (map, visible) => {
    parcelasVisibleRef.current = visible;
    if (!map) return;
    syncParcelLayersVisibility(map);
  };

  const applyParcelasFilter = (map, filterValue) => {
    if (!canAccessMapStyle(map)) return;
    const trimmed = filterValue.trim();
    loadedPmtilesIdsRef.current.forEach((id) => {
      const fillLayerId = `self-hosted-vector-fill-${id}`;
      const layerId     = `self-hosted-vector-layer-${id}`;
      if (!map.getLayer(fillLayerId) && !map.getLayer(layerId)) return;
      const filter = trimmed === '' ? null : [
        'all', ['has', 'state'],
        ['any',
          ['==', ['downcase', ['get', 'state']], trimmed.toLowerCase()],
          ['in', trimmed.toLowerCase(), ['downcase', ['get', 'state']]],
        ],
      ];
      if (map.getLayer(fillLayerId)) map.setFilter(fillLayerId, filter);
      if (map.getLayer(layerId))     map.setFilter(layerId,     filter);
    });
  };

  const getInteractiveParcelLayerIds = useCallback(
    () => Array.from(loadedPmtilesIdsRef.current).map((id) => `self-hosted-vector-fill-${id}`),
    []
  );

  const getParcelPointsLayerIds = useCallback(
    () => Array.from(loadedPointsIdsRef.current).map(parcelPointsLayerId),
    [],
  );

  /**
   * Sources de parcelas cargados para la vista actual, para leer valores del
   * tile con `querySourceFeatures`. A zoom bajo, donde no hay polígonos, manda
   * la capa de puntos: mismo contrato de atributos.
   * @returns {Array<{sourceId: string, sourceLayer: string}>}
   */
  const getActiveParcelSources = useCallback((map) => {
    if (!canAccessMapStyle(map)) return [];

    const polygons = getMatchingPolygonConfigs(map, getPolygonConfigs(pmtilesConfigRef.current))
      .map((cfg) => ({
        sourceId: `self-hosted-vector-${cfg.id}`,
        sourceLayer: cfg.layer || PARCEL_POLYGONS_SOURCE_LAYER,
      }))
      .filter(({ sourceId }) => map.getSource(sourceId));
    if (polygons.length > 0) return polygons;

    return pointsTilesetsRef.current
      .filter((cfg) => viewportIntersectsBbox(map, cfg.bounds))
      .map((cfg) => ({
        sourceId: parcelPointsSourceId(cfg.id),
        sourceLayer: cfg.layer || PARCEL_POINTS_SOURCE_LAYER,
      }))
      .filter(({ sourceId }) => map.getSource(sourceId));
  }, []);

  /**
   * Asegura las capas de puntos en viewport y sincroniza visibilidad
   * (independiente del zoom 10 de polígonos).
   * @param {Function} [onPointsLayerReady] se invoca tras crear/cargar las capas (re-aplicar filtros).
   */
  const syncParcelPointsView = useCallback((map, { hasActiveFilters = false, onPointsLayerReady } = {}) => {
    filterViewStateRef.current = { hasActiveFilters };
    if (!canAccessMapStyle(map)) return;

    const finish = () => syncParcelLayersVisibility(map);

    if (hasActiveFilters && pointsTilesetsRef.current.length > 0) {
      const pending = pointsTilesetsRef.current.filter((cfg) => (
        viewportIntersectsBbox(map, cfg.bounds) && !map.getLayer(parcelPointsLayerId(cfg.id))
      ));

      if (pending.length === 0) {
        onPointsLayerReady?.(map);
        finish();
        return;
      }

      void ensureParcelPointsLayers(map).then(() => {
        onPointsLayerReady?.(map);
        finish();
      });
      return;
    }

    finish();
  }, [ensureParcelPointsLayers]);

  return {
    fetchPmtilesConfig,
    ensurePmtilesLayerForView,
    scheduleEnsurePmtilesLayerForView,
    toggleParcelas,
    applyParcelasFilter,
    getInteractiveParcelLayerIds,
    getParcelPointsLayerIds,
    getActiveParcelSources,
    syncParcelPointsView,
  };
}